// eslint-disable-next-line import/no-unresolved
import { toast } from 'react-toastify';

import api from '@config/axiosConfig';
import { configuredFetchBaseQuery } from '@config/rtkQuery';
import { capacityApi } from '@reducers/capacity';
import { numberApi } from '@reducers/number/number';
import { planDetailsApi } from '@reducers/plan';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

export const cartApi = createApi({
  reducerPath: 'cartApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getDidsForPlan: builder.query({
      query: ({ id, type, prefixID }) => ({
        url: `customer/cart/dids/plans/${id}/type/${type}/prefixes/${prefixID}`,
      }),
      providesTags: (result, error, { id, type, prefixID }) => [
        { tag: 'CartDids', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    addDidToCart: builder.mutation({
      query: ({ id, tos }) => ({
        url: `customer/cart/dids/tos/${tos}/add`,
        method: 'POST',
        body: { did: id },
      }),
      invalidatesTags: (result, error, { planID, type, prefixID }) =>
        error
          ? []
          : [
              { tag: 'CartDids', type: `${planID}-${prefixID}-${type}` },
              { tag: 'Cart', type: `${planID}-${prefixID}-${type}` },
            ],
      async onQueryStarted(
        { planID, type, prefixID },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            planDetailsApi.util.invalidateTags([
              {
                tag: 'PlanPrefixAvailableDids',
                type: `${planID}-${prefixID}-${type}`,
              },
              { tag: 'PlanBackordersInfo', type: prefixID },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    removeDidFromCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/dids/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { planID, type, prefixID }) => [
        { tag: 'CartDids', type: `${planID}-${prefixID}-${type}` },
        { tag: 'Cart', type: `${planID}-${prefixID}-${type}` },
        'CheckoutSummary',
      ],
      async onQueryStarted(
        { planID, type, prefixID },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            planDetailsApi.util.invalidateTags([
              {
                tag: 'PlanPrefixAvailableDids',
                type: `${planID}-${prefixID}-${type}`,
              },
              { tag: 'PlanBackordersInfo', type: prefixID },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    cancelDidsFromPlanCart: builder.mutation({
      query: (arg) => {
        const { id, type, prefixID } = arg;
        return {
          url: `customer/cart/dids/plans/${id}/type/${type}/prefixes/${prefixID}/cancel`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, { id, type, prefixID }) => [
        'CheckoutSummary',
        { tag: 'CartDids', type: `${id}-${prefixID}-${type}` },
      ],
      async onQueryStarted(
        { id, type, prefixID },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            planDetailsApi.util.invalidateTags([
              {
                tag: 'PlanPrefixAvailableDids',
                type: `${id}-${prefixID}-${type}`,
              },
              { tag: 'PlanBackordersInfo', type: prefixID },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    saveDidsFromCart: builder.mutation({
      query: (arg) => {
        const { data } = arg;
        const body = [];
        Object.values(data).forEach((item) => {
          body.push({
            id: item.id,
            profile: item.profileID,
            trunk: item.trunkID,
            use_case: item.didServiceType,
          });
        });

        return {
          url: `customer/cart/dids/save`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, { id, type, prefixID }) => [
        'CheckoutSummary',
        { tag: 'CartDids', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    getCheckoutSummary: builder.query({
      query: () => ({ url: `customer/cart/check-out` }),
      transformResponse: (response) => {
        const { data, total, restrictions } = response;
        if (data.length === 0) {
          return { results: data, restrictions };
        }
        data.push({
          amount: 'Total',
          mrc_price: total.mrc,
          nrc_price: total.nrc,
        });
        return { results: data, restrictions };
      },
      providesTags: ['CheckoutSummary'],
    }),
    deleteCartContent: builder.mutation({
      query: () => ({ url: `customer/cart`, method: 'DELETE' }),
      invalidatesTags: (result, error) => (error ? [] : ['CheckoutSummary']),
    }),
    processOrderCart: builder.mutation({
      query: () => ({ url: `customer/cart/check-out`, method: 'POST' }),
      invalidatesTags: (result, error) => (error ? [] : ['CheckoutSummary']),
    }),
    addBackorderToCart: builder.mutation({
      query: ({ amount, plan, type, prefixID }) => ({
        url: `customer/cart/backorders/add`,
        method: 'POST',
        body: { amount, plan, plan_type: type, area: prefixID },
      }),
      invalidatesTags: (result, error, { prefixID, plan: planID, type }) => [
        { tag: 'CartBackorder', type: prefixID },
        { tag: 'Cart', type: `${planID}-${prefixID}-${type}` },
      ],
    }),
    removeBackorderFromCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/backorders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { prefixID, planID, type }) => [
        { tag: 'CartBackorder', type: prefixID },
        { tag: 'Cart', type: `${planID}-${prefixID}-${type}` },
      ],
    }),
    reduceBackorderAmount: builder.mutation({
      query: ({ id, type, areaID }) => ({
        url: `customer/cart/backorders/plans/${id}/type/${type}/area/${areaID}/reduce`,
        method: 'PATCH',
      }),
      invalidatesTags: ['CheckoutSummary'],
    }),
    increaseBackorderAmount: builder.mutation({
      query: ({ id, type, areaID }) => ({
        url: `customer/cart/backorders/plans/${id}/type/${type}/area/${areaID}/increase`,
        method: 'PATCH',
      }),
      invalidatesTags: ['CheckoutSummary'],
    }),
    getBackordersForPlan: builder.query({
      query: ({ id, type, prefixID }) => ({
        url: `customer/cart/backorders/plans/${id}/type/${type}/prefixes/${prefixID}`,
      }),
      transformResponse: (response) => {
        if (!response.amount) {
          return [];
        }
        return [{ did: `Backorder`, ...response }];
      },
      providesTags: (result, error, { prefixID }) => [
        { tag: 'CartBackorder', type: prefixID },
      ],
    }),
    cancelBackordersFromPlanCart: builder.mutation({
      query: ({ id, type, prefixID }) => ({
        url: `customer/cart/backorders/plans/${id}/type/${type}/prefixes/${prefixID}/cancel`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id, type, prefixID }) => [
        'CheckoutSummary',
        { tag: 'CartDids', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    saveBackordersFromCart: builder.mutation({
      query: ({ data }) => {
        const body = [];
        Object.values(data).forEach((item) => {
          body.push({
            id: item.id,
            profile: item.profileID,
            trunk: item.trunkID,
            use_case: item.didServiceType,
          });
        });

        return {
          url: `customer/cart/backorders/save`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, { id, type, prefixID }) => [
        'CheckoutSummary',
        { tag: 'CartDids', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    getForPlan: builder.query({
      query: ({ id, type, prefixID }) => ({
        url: `customer/cart/plans/${id}/type/${type}/prefixes/${prefixID}`,
      }),
      transformResponse: (response) =>
        response.map((item) => ({
          ...item,
          did: item.is_backorder ? 'Backorder' : item.did,
        })),
      providesTags: (result, error, { id, type, prefixID }) => [
        { tag: 'Cart', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    getConfiguredForPlan: builder.query({
      query: ({ id, type, prefixID }) => ({
        url: `customer/cart/plans/${id}/type/${type}/prefixes/${prefixID}/configured`,
      }),
      transformResponse: (response) =>
        response.map((item) => ({
          ...item,
          did: item.is_backorder ? 'Backorder' : item.did,
        })),
      providesTags: (result, error, { id, type, prefixID }) => [
        { tag: 'Cart', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    getForPSTNUpgrade: builder.query({
      query: ({ id, type }) => ({
        url: `customer/cart/dids/plans/${id}/type/${type}/pstn-upgrade`,
      }),
      providesTags: (result, error, { customer }) => [
        { tag: 'CartPSTNUpgrade', type: customer },
      ],
    }),
    addPSTNUpgradesToCart: builder.mutation({
      query: ({ dids }) => ({
        url: `customer/cart/dids/pstn-upgrade`,
        method: 'POST',
        body: dids,
      }),
      invalidatesTags: (result, error, { customer }) =>
        error ? [] : [{ tag: 'CartPSTNUpgrade', type: customer }],
      async onQueryStarted({ customer }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            numberApi.util.invalidateTags([
              {
                tag: 'CustomerPSTNUpgradeDids',
                type: customer,
              },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    removePSTNUpgradeDidFromCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/dids/pstn-upgrade/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { customer }) =>
        error ? [] : [{ tag: 'CartPSTNUpgrade', type: customer }],
      async onQueryStarted({ customer }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            numberApi.util.invalidateTags([
              {
                tag: 'CustomerPSTNUpgradeDids',
                type: customer,
              },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    cancelPSTNUpgradeDidFromPlanCart: builder.mutation({
      query: ({ id, prefixID }) => ({
        url: `customer/cart/dids/plans/${id}/prefixes/${prefixID}/pstn-upgrade/cancel`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { customer }) =>
        error
          ? []
          : ['CheckoutSummary', { tag: 'CartPSTNUpgrade', type: customer }],
      async onQueryStarted({ customer }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            numberApi.util.invalidateTags([
              {
                tag: 'CustomerPSTNUpgradeDids',
                type: customer,
              },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
  }),
});

export const cancellationCartApi = createApi({
  reducerPath: 'cancellationCartApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getList: builder.query({
      query: () => ({
        url: `customer/cart/cancellations`,
      }),
      providesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      transformResponse: (response) =>
        response
          ? response.map((item) => ({
              ...item,
              isDedicatedChannel: item.type_of_service === 'Dedicated Channels',
            }))
          : response,
    }),
    addDidToCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/cancellations/add`,
        method: 'POST',
        body: { ids: [id] },
      }),
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'CancellationCart', type: login }],
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            numberApi.util.invalidateTags([
              { type: login, tag: 'CustomerDids' },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    addDidsToCart: builder.mutation({
      query: ({ dids }) => ({
        url: `customer/cart/cancellations/add`,
        method: 'POST',
        body: { ids: dids },
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            numberApi.util.invalidateTags([
              { type: login, tag: 'CustomerDids' },
            ])
          );
        } catch (err) {
          // Already handled in matchers
        }
      },
    }),
    clearCart: builder.mutation({
      query: () => ({
        url: `customer/cart/cancellations/clear`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
    }),
    removeDidFromCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/cancellations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
    }),
    processCart: builder.mutation({
      query: () => ({
        url: `customer/cart/cancellations/process`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            numberApi.util.invalidateTags([
              { type: login, tag: 'CustomerDids' },
            ])
          );
        } catch (err) {
          /* empty */
        }
      },
    }),
    addDedicatedChannelToCart: builder.mutation({
      query: ({ did }) => ({
        url: `customer/cart/cancellations/add-dedicated-channel`,
        method: 'POST',
        body: { did },
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(capacityApi.util.invalidateTags(['DedicatedChannelsDids']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    removeDedicatedChannelFromCart: builder.mutation({
      query: ({ did }) => ({
        url: `customer/cart/cancellations/${did}/remove-dedicated-channel`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(capacityApi.util.invalidateTags(['DedicatedChannelsDids']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    deleteChannelOrderFromCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/cancellations/${id}/delete-channel-order`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(capacityApi.util.invalidateTags(['DedicatedChannelsDids']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    clearDedicatedChannelsCart: builder.mutation({
      query: () => ({
        url: `customer/cart/cancellations/clear-dedicated-channels`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'CancellationCart', type: login },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(capacityApi.util.invalidateTags(['DedicatedChannelsDids']));
        } catch (err) {
          /* empty */
        }
      },
    }),
  }),
});

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getOpen: builder.query({
      query: (arg) => {
        const url = 'customer/orders/open';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: ['Open Orders'],
    }),
    getHistory: builder.query({
      query: (arg) => {
        const url = 'customer/orders/history';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: ['Orders History'],
    }),
    getIncomplete: builder.query({
      query: (arg) => {
        const url = 'customer/orders/incomplete';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: ['Incomplete Orders'],
    }),
    getStatuses: builder.query({
      query: () => ({ url: `order-statuses` }),
      providesTags: ['Order Statuses'],
    }),
    getDetails: builder.query({
      query: (arg) => {
        const { uuid } = arg;
        return { url: `customer/orders/${uuid}` };
      },
      transformResponse: (response) => {
        response.profiles = response.profiles.map((item) => ({
          ...item,
          location: item.id ? `/profiles/edit/${item.id}` : null,
        }));
        return response;
      },
      providesTags: (result, error, { uuid }) => [
        { tag: 'OrderDetails', uuid },
      ],
    }),
  }),
});

export const dedicatedChannelsCartApi = createApi({
  reducerPath: 'dedicatedChannelsCartApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    addChannelToCart: builder.mutation({
      query: ({ did }) => ({
        url: `customer/cart/dedicated-channels/add`,
        method: 'POST',
        body: { did },
      }),
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            capacityApi.util.invalidateTags([
              {
                tag: 'DedicatedChannelsDidsOrder',
                type: login,
              },
            ])
          );
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    clearChannelCart: builder.mutation({
      query: () => ({
        url: `customer/cart/dedicated-channels/clear`,
        method: 'DELETE',
      }),
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            capacityApi.util.invalidateTags([
              {
                tag: 'DedicatedChannelsDidsOrder',
                type: login,
              },
            ])
          );
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    removeChannelFromCart: builder.mutation({
      query: ({ did }) => ({
        url: `customer/cart/dedicated-channels/remove`,
        method: 'DELETE',
        body: { did },
      }),
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            capacityApi.util.invalidateTags([
              {
                tag: 'DedicatedChannelsDidsOrder',
                type: login,
              },
            ])
          );
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    cancelPlanChannelsFromCart: builder.mutation({
      query: ({ id, type }) => ({
        url: `customer/cart/dedicated-channels/plans/${id}/type/${type}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ login }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            capacityApi.util.invalidateTags([
              {
                tag: 'DedicatedChannelsDidsOrder',
                type: login,
              },
            ])
          );
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
    }),
  }),
});

export const sharedChannelsCartApi = createApi({
  reducerPath: 'sharedChannelsCartApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getCart: builder.query({
      query: (arg) => {
        const url = 'customer/cart/shared-channels';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      transformResponse: (response) => {
        const { results } = response;
        results.forEach((item) => {
          // Defaults
          item.canAdd = false;
          item.canRemove =
            item.current_channel_orders > 0 ||
            Math.abs(item.current_channel_orders) < item.channel_amount;

          if (
            item.current_channel_orders + item.channel_amount >=
            item.max_channel_amount
          ) {
            item.canAdd = false;
            return;
          }
          if (
            item.current_channel_orders + item.channel_amount <
            item.max_channel_amount
          ) {
            item.canAdd = true;
          }
        });
        return response;
      },
      providesTags: (result, error, { login }) => [
        { tag: 'SharedChannelsOrder', type: login },
      ],
    }),
    addChannelToCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/shared-channels/${id}`,
        method: 'POST',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'SharedChannelsOrder', type: login }],
    }),
    clearChannelCart: builder.mutation({
      query: () => ({
        url: `customer/cart/shared-channels/clear`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'SharedChannelsOrder', type: login }],
    }),
    removeChannelFromCart: builder.mutation({
      query: ({ id }) => ({
        url: `customer/cart/shared-channels/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'SharedChannelsOrder', type: login }],
    }),
    cancelPlanChannelsFromCart: builder.mutation({
      query: ({ id, type }) => ({
        url: `customer/cart/shared-channels/plans/${id}/type/${type}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(['CheckoutSummary']));
        } catch (err) {
          /* empty */
        }
      },
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'SharedChannelsOrder', type: login }],
    }),
  }),
});

export const getOrderCart = createAsyncThunk(
  'order/cart/',
  async (_, thunkAPI) => {
    try {
      const res = await api({ thunkAPI }).get(`/order/cart`);
      if (res.status === 200) {
        return res.data;
      }
      return thunkAPI.rejectWithValue(res.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const addChannelsToCart = createAsyncThunk(
  'order/cart/add/channels',
  async ({ channels }, thunkAPI) => {
    const body = JSON.stringify({ channels });
    try {
      const res = await api({ thunkAPI }).post(
        '/order/cart/add/channels',
        body
      );
      if (res.status === 200) {
        const { dispatch } = thunkAPI;
        dispatch(getOrderCart());
        return res.data;
      }
      return thunkAPI.rejectWithValue(res.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const addChannelsToCancellationCart = createAsyncThunk(
  'order/cart/add/channels/cancel',
  async ({ channels }, thunkAPI) => {
    const body = JSON.stringify({ channels });
    try {
      const res = await api({ thunkAPI }).post(
        '/order/cart/add/channels/cancel',
        body
      );
      if (res.status === 200) {
        const { dispatch } = thunkAPI;
        dispatch(getOrderCart());
        return res.data;
      }
      return thunkAPI.rejectWithValue(res.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

const initialState = {
  dids: [],
  channels: [],
  cart: {
    isLoading: false,
    data: [],
    restrictions: [],
    itemAmount: 0,
  },
  cartProcess: { isLoading: false, message: '' },
  open: { isLoading: false, data: [] },
  history: { isLoading: false, data: [] },
  status: { isLoading: false, data: [] },
  cartAdd: { isLoading: false, success: null },
  cancelCartAdd: { isLoading: false, success: null },
  details: { isLoading: false, data: {} },
  toast: {},
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  extraReducers: (builder) =>
    builder
      .addCase(getOrderCart.pending, (state) => {
        state.cart.isLoading = true;
      })
      .addCase(getOrderCart.fulfilled, (state, action) => {
        state.cart.isLoading = false;
        const total = { dids: { amount: 'Total' }, ...action.payload.total };
        state.cart.data = [...action.payload.data, total];
        state.cart.restrictions = action.payload.data.filter((item) =>
          Object.keys(item).includes('restrictions') ? item : null
        );
        state.cart.itemAmount = action.payload.total.amount;
      })
      .addCase(getOrderCart.rejected, (state) => {
        state.cart.isLoading = false;
      })
      .addCase(addChannelsToCart.pending, (state) => {
        state.cartAdd.isLoading = true;
      })
      .addCase(addChannelsToCart.fulfilled, (state, action) => {
        state.cartAdd.isLoading = false;
        state.cartAdd.success = action.payload;
      })
      .addCase(addChannelsToCart.rejected, (state) => {
        state.cartAdd.isLoading = false;
      })
      .addCase(addChannelsToCancellationCart.pending, (state) => {
        state.cancelCartAdd.isLoading = true;
      })
      .addCase(addChannelsToCancellationCart.fulfilled, (state, action) => {
        state.cancelCartAdd.isLoading = false;
        state.cancelCartAdd.success = action.payload;
      })
      .addCase(addChannelsToCancellationCart.rejected, (state) => {
        state.cancelCartAdd.isLoading = false;
      })
      .addMatcher(
        cartApi.endpoints.addBackorderToCart.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] = toast.loading(
            'Adding backorder...'
          );
        }
      )
      .addMatcher(
        cartApi.endpoints.addBackorderToCart.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Backorder added.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cartApi.endpoints.addBackorderToCart.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: action.payload?.data
              ? action.payload?.data
              : 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cartApi.endpoints.removeBackorderFromCart.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] = toast.loading(
            'Removing backorder...'
          );
        }
      )
      .addMatcher(
        cartApi.endpoints.removeBackorderFromCart.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Backorder removed.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cartApi.endpoints.removeBackorderFromCart.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cancellationCartApi.endpoints.addDidToCart.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] =
            toast.loading('Cancelling DID...');
        }
      )
      .addMatcher(
        cancellationCartApi.endpoints.addDidToCart.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'DID added to cancellation cart',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cancellationCartApi.endpoints.addDidToCart.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: action.payload?.data
              ? action.payload?.data
              : 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cancellationCartApi.endpoints.addDidsToCart.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] =
            toast.loading('Cancelling DIDs...');
        }
      )
      .addMatcher(
        cancellationCartApi.endpoints.addDidsToCart.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'DIDs added to cancellation cart.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        cancellationCartApi.endpoints.addDidsToCart.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: action.payload?.data
              ? action.payload?.data
              : 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      ),
});
export default orderSlice.reducer;
